// Secteurs de collecte et points de collecte sur la carte du territoire (couche /carte/proprete pour
// l'emprise affichée) : emprises des secteurs dessinées, points marqués et numérotés dans la liste
// voisine ; création d'un secteur (emprise saisie en positions) et d'un point (position prise d'un clic
// sur la carte ou saisie) par le responsable technique.
import { CODES_TYPES_POINT, schemaCreationPointCollecte, schemaCreationSecteur, TYPES_POINT_COLLECTE } from '@cc/contrats';
import { Bandeau, Bouton, CarteTerritoire, Champ, Dialogue, EtatVide, LienBouton, PuceEtat, Selection, Squelette, Tableau, ZoneTexte, numeroMarqueur, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, MapPinned, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useEchellesTerritoire } from '../marches/carte';
import { texteVersEmprise } from '../marches/PageAutorisation';
import { creerPoint, creerSecteur, INVALIDER_PROPRETE, useCarteProprete, usePoints, useSecteurs } from './api';
import { marqueursPoints, surfacesSecteurs } from './logique';
import type { PointCollecte, Position, Secteur, TypePointCollecte } from './types';
import styles from './proprete.module.css';
import marches from '../marches/marches.module.css';
import commun from '../../commun/commun.module.css';

function FormulaireSecteur({ onFermer }: { onFermer: () => void }) {
  const [v, setV] = useState({ nom: '', emprise: '' });
  const [probleme, setProbleme] = useState<string | null>(null);
  const action = useAction({
    executer: () => {
      const corps = schemaCreationSecteur.safeParse({ nom: v.nom, emprise: v.emprise.trim() ? texteVersEmprise(v.emprise) ?? undefined : undefined });
      if (!corps.success) {
        setProbleme(corps.error.issues.map((i) => `${i.path.join('.') || 'emprise'} : ${i.message}`).join(' ; '));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setProbleme(null);
      return creerSecteur(corps.data);
    },
    succes: (s) => `Secteur ${s.nom} créé, code ${s.code}.`,
    invalider: INVALIDER_PROPRETE,
    apres: onFermer,
  });
  return (
    <Dialogue ouvert titre="Nouveau secteur" description="Le code est généré à partir des initiales du nom. L'emprise se saisit par une position « longitude latitude » par ligne (au moins trois) ; l'anneau est fermé automatiquement." onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Créer le secteur</Bouton></>}>
      <Champ libelle="Nom" value={v.nom} onChange={(e) => setV((x) => ({ ...x, nom: e.target.value }))} required />
      <ZoneTexte libelle="Emprise (une position par ligne)" value={v.emprise} onChange={(e) => setV((x) => ({ ...x, emprise: e.target.value }))} rows={4} facultatif aide="Exemple : 9.451 0.392" />
      {probleme ? <Bandeau type="erreur">{probleme}</Bandeau> : null}
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function FormulairePoint({ secteurs, position, onFermer }: { secteurs: Secteur[]; position: Position | null; onFermer: () => void }) {
  const [v, setV] = useState({ nom: '', type: 'bac' as TypePointCollecte, secteur_id: '', longitude: position ? String(position.longitude) : '', latitude: position ? String(position.latitude) : '', frequence: '1' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const corps = schemaCreationPointCollecte.safeParse({ nom: v.nom, type: v.type, secteur_id: v.secteur_id || undefined, position: { longitude: Number(v.longitude), latitude: Number(v.latitude) }, frequence_hebdomadaire: Number(v.frequence) });
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0]), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return creerPoint(corps.data);
    },
    succes: (p) => `Point ${p.nom} créé, code ${p.code}.`,
    invalider: INVALIDER_PROPRETE,
    apres: onFermer,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  return (
    <Dialogue ouvert titre="Nouveau point de collecte" description="Un clic sur la carte avant d'ouvrir ce formulaire remplit la position." onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Créer le point</Bouton></>}>
      <Champ libelle="Nom" value={v.nom} onChange={(e) => setV((x) => ({ ...x, nom: e.target.value }))} erreur={champ('nom')} required />
      <div className={styles.grilleFormulaire}>
        <Selection libelle="Type" options={CODES_TYPES_POINT.map((t) => ({ valeur: t, libelle: TYPES_POINT_COLLECTE[t] }))} value={v.type} onChange={(e) => setV((x) => ({ ...x, type: e.target.value as TypePointCollecte }))} />
        <Selection libelle="Secteur" options={secteurs.map((s) => ({ valeur: s.id, libelle: `${s.code} · ${s.nom}` }))} optionVide="Hors secteur" value={v.secteur_id} onChange={(e) => setV((x) => ({ ...x, secteur_id: e.target.value }))} facultatif />
        <Champ libelle="Longitude" value={v.longitude} onChange={(e) => setV((x) => ({ ...x, longitude: e.target.value }))} erreur={champ('position')} required />
        <Champ libelle="Latitude" value={v.latitude} onChange={(e) => setV((x) => ({ ...x, latitude: e.target.value }))} required />
        <Champ libelle="Passages par semaine" type="number" value={v.frequence} onChange={(e) => setV((x) => ({ ...x, frequence: e.target.value }))} erreur={champ('frequence_hebdomadaire')} />
      </div>
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PageSecteurs() {
  const { contexte } = useSession();
  const t = useEchellesTerritoire();
  const couche = useCarteProprete(t.bbox);
  const secteurs = useSecteurs();
  const points = usePoints();
  const [actif, setActif] = useState<string | null>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const [dialogue, setDialogue] = useState<'secteur' | 'point' | null>(null);
  const marqueurs = useMemo(() => marqueursPoints(couche.data), [couche.data]);
  const surfaces = useMemo(() => surfacesSecteurs(couche.data), [couche.data]);
  const peutCreer = peut(contexte, 'dechets', 'creer');
  const colonnesSecteurs: ColonneTableau<Secteur>[] = [
    { id: 'code', entete: 'Code', enTeteLigne: true, masquable: false, cellule: (s) => <span className={commun.reference}>{s.code}</span>, valeurTri: (s) => s.code },
    { id: 'nom', entete: 'Secteur', cellule: (s) => s.nom, valeurTri: (s) => s.nom },
    { id: 'points', entete: 'Points', numerique: true, cellule: (s) => s.statistiques?.points.toLocaleString('fr-FR') ?? '0', valeurTri: (s) => s.statistiques?.points ?? 0 },
    { id: 'tournees', entete: 'Tournées', numerique: true, cellule: (s) => s.statistiques?.tournees.toLocaleString('fr-FR') ?? '0', valeurTri: (s) => s.statistiques?.tournees ?? 0 },
    { id: 'emprise', entete: 'Emprise', cellule: (s) => (s.emprise ? <PuceEtat ton="ok" libelle="Dessinée" /> : <PuceEtat ton="neutre" libelle="Non dessinée" />) },
  ];
  const colonnesPoints: ColonneTableau<PointCollecte>[] = [
    { id: 'code', entete: 'Code', enTeteLigne: true, masquable: false, cellule: (p) => <span className={commun.reference}>{p.code}</span>, valeurTri: (p) => p.code },
    { id: 'nom', entete: 'Point', cellule: (p) => p.nom, valeurTri: (p) => p.nom },
    { id: 'type', entete: 'Type', cellule: (p) => p.type_libelle, valeurTri: (p) => p.type },
    { id: 'secteur', entete: 'Secteur', cellule: (p) => p.secteur?.nom ?? 'hors secteur' },
    { id: 'frequence', entete: 'Passages par semaine', numerique: true, cellule: (p) => p.frequence_hebdomadaire.toLocaleString('fr-FR'), valeurTri: (p) => p.frequence_hebdomadaire },
    { id: 'position', entete: 'Position', cellule: (p) => <span className="cc-mono">{p.position.longitude.toFixed(4)} {p.position.latitude.toFixed(4)}</span> },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Déchets et propreté"
        titre="Secteurs et points de collecte"
        description="Emprises des secteurs et points de collecte sur la carte du territoire ; un clic sur la carte retient une position pour un nouveau point."
        actions={
          <>
            <LienBouton href="/proprete" icone={ArrowLeft} variante="secondaire">Tournées</LienBouton>
            {peutCreer ? <Bouton variante="secondaire" icone={Plus} onClick={() => setDialogue('secteur')}>Nouveau secteur</Bouton> : null}
            {peutCreer ? <Bouton variante="principal" icone={Plus} onClick={() => setDialogue('point')}>Nouveau point</Bouton> : null}
          </>
        }
      />
      <div className={marches.disposition}>
        <div className={marches.carteCadre}>
          {t.echelles?.mention ? <Bandeau type="info">{t.echelles.mention}</Bandeau> : null}
          {couche.error ? <Bandeau type="info">La couche de la propreté n&apos;est pas disponible (droits ou module inactif) : la carte s&apos;affiche sans ses marqueurs.</Bandeau> : null}
          {t.pret && t.echelles && t.echelle ? (
            <CarteTerritoire titre="Secteurs et points de collecte" echelles={t.echelles.echelles} echelle={t.echelle} onEchelle={t.setEchelle} marqueurs={marqueurs} marqueurActif={actif} onMarqueur={setActif} onSaisie={(p) => setPosition({ longitude: p[0], latitude: p[1] })} surfaces={surfaces} libellesTon={{ ouvert: 'Point de collecte' }} libelleObjets={{ singulier: 'point de collecte', pluriel: 'points de collecte' }} hauteur="min(60vh, 560px)" />
          ) : (
            <Squelette hauteurBloc={400} libelle="Chargement de la carte" />
          )}
          <p className={marches.aide}>
            {position ? <>Position retenue pour un nouveau point : <span className="cc-mono">{position.longitude.toFixed(5)} {position.latitude.toFixed(5)}</span>.</> : 'Les emprises des secteurs sont dessinées ; chaque point de collecte est marqué.'}
          </p>
        </div>
        <aside className={marches.cote} aria-label="Points sur la carte">
          <ol className={marches.listeCarte} aria-label="Points sur la carte">
            {marqueurs.map((m) => (
              <li key={m.id}>
                <button type="button" className={marches.elementCarte} aria-pressed={actif === m.id} onClick={() => setActif(actif === m.id ? null : m.id)}>
                  <span className={marches.numero} aria-hidden>{numeroMarqueur(m.numero)}</span>
                  <span>{m.libelle}</span>
                </button>
              </li>
            ))}
          </ol>
          {t.bbox && marqueurs.length === 0 && !couche.isPending ? <p className={marches.aide}>Aucun point de collecte dans cette emprise.</p> : null}
        </aside>
      </div>
      <EtatRequete chargement={secteurs.isPending} erreur={secteurs.error} onReessayer={() => void secteurs.refetch()} lot="L3-B" fonction="La liste des secteurs">
        {(secteurs.data ?? []).length === 0 ? <EtatVide titre="Aucun secteur" icone={MapPinned}><p>Aucun secteur de collecte n&apos;est défini dans cette collectivité.</p></EtatVide> : <Tableau legende="Secteurs" colonnes={colonnesSecteurs} lignes={secteurs.data ?? []} cleLigne={(s) => s.id} cleColonnes="proprete-secteurs" />}
      </EtatRequete>
      <EtatRequete chargement={points.isPending} erreur={points.error} onReessayer={() => void points.refetch()} lot="L3-B" fonction="La liste des points de collecte">
        {(points.data ?? []).length === 0 ? null : <Tableau legende="Points de collecte" colonnes={colonnesPoints} lignes={points.data ?? []} cleLigne={(p) => p.id} cleColonnes="proprete-points" />}
      </EtatRequete>
      {dialogue === 'secteur' ? <FormulaireSecteur onFermer={() => setDialogue(null)} /> : null}
      {dialogue === 'point' ? <FormulairePoint secteurs={secteurs.data ?? []} position={position} onFermer={() => setDialogue(null)} /> : null}
    </>
  );
}
